import React, { useState, useEffect } from 'react';
import { Typography, Box, Stack, Divider, IconButton, CircularProgress } from '@mui/material';
import { some } from "lodash";
import useZustandStore from "zustandStore";
import { isLicenseManagementEnabled } from "utils/licenseFeature";

import { protocolTypeOption, scopeLevelOption, adoptionSourceType } from "../../const";

import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import CaretRightFilled from "@ant-design/icons/CaretRightFilled";
import WarningFilled from "@ant-design/icons/WarningFilled";
import CloseOutlined from "@ant-design/icons/CloseOutlined";

import AdoptionTitle from './components/AdoptionTitle';
import DeviceTable from "./components/DeviceTable";
import SiteSetting from "./components/SiteSetting";
import IpSettings from './components/IpSettings';
import Authentication from './components/Authentication';
import Operation from "./components/Operation";

const componentsOption = {
  [adoptionSourceType.byRow]: {
    steps: 2,
    components: [
      ['AdoptionTitle', 'DeviceTable', 'SiteSetting', 'Operation'],
      ['AdoptionTitle', 'DeviceTable', 'Operation'],
    ]
  },
  [adoptionSourceType.byCheckboxWithIpSettings]: {
    steps: 2,
    components: [
      ['AdoptionTitle', 'DeviceTable', 'IpSettings', 'Operation'],
      ['AdoptionTitle', 'DeviceTable', 'Operation'],
    ]
  },
  [adoptionSourceType.byCheckboxWithAuthenticate]: {
    steps: 2,
    components: [
      ['AdoptionTitle', 'DeviceTable', 'Authentication', 'Operation'],
      ['AdoptionTitle', 'DeviceTable', 'Operation'],
    ]
  },
  [adoptionSourceType.byCheckboxWithAdoption]: {
    steps: 2,
    components: [
      ['AdoptionTitle', 'DeviceTable', 'SiteSetting', 'Operation'],
      ['AdoptionTitle', 'DeviceTable', 'Operation'],
    ]
  },
};

const ModalAdoption = (props) => {
  const portalConfig = useZustandStore((state) => state.portalConfig);
  const licenseManagementEnabled = isLicenseManagementEnabled(portalConfig);
  const {
    showModalAdoption, closeOpenModal,
    scopeLevel, sites,
    devices, selectedDevice, selectedDevices,
    adoptionSource, sendAdoptionRequest,
    readyDevices, setReadyDevices, results,
    isNotSupportDNSDevice,
    showConfirmServiceChangeModal, setShowConfirmServiceChangeModal,
    sendPreConfirmRequest, preConfirmResult, isPreConfirmLoading, setPreConfirmResult
  } = props;

  // State
  const [currentStep, setCurrentStep] = useState(0);
  const [activePreConfirmData, setActivePreConfirmData] = useState(null);
  const [components, setComponents] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [ipSettingsStatus, setIpSettingsStatus] = useState(false);
  const [username, setUsername] = useState(''); // Authentication username
  const [password, setPassword] = useState(''); // Authentication password
  const [authenticateStatus, setAuthenticateStatus] = useState(false); // Authentication credentials status
  const [hasSshDevices, setHasSshDevices] = useState(false); // Flag to indicate presence of SSH devices
  const [sshUsername, setSshUsername] = useState(''); // SSH username
  const [sshPassword, setSshPassword] = useState(''); // SSH password
  const [sshStatus, setSshStatus] = useState(true); // SSH credentials status
  const [ipModeOptions, setIpModeOptions] = useState([]);
  const [ipAddress, setIpAddress] = useState('');
  const [subnetMask, setSubnetMask] = useState('');
  const [gateway, setGateway] = useState('');
  const [dns1, setDns1] = useState('');
  const [dns2, setDns2] = useState('');

  // Method
  const isAllNotSupportDNSDevice = () => {
    if (!selectedDevices || selectedDevices.length === 0) {
      return false;
    }

    return selectedDevices.every((device) => isNotSupportDNSDevice(device.modelName));
  }

  const getButtonTitle = () => {
    if (adoptionSource === adoptionSourceType.byRow) {
      return "Next";
    }

    if (adoptionSource === adoptionSourceType.byCheckboxWithIpSettings) {
      if (currentStep === 0) {
        return "Next";
      }
    }

    if (adoptionSource === adoptionSourceType.byCheckboxWithAuthenticate) {
      if (currentStep === 0) {
        return "Next";
      }
    }

    if (adoptionSource === adoptionSourceType.byCheckboxWithAdoption) {
      if (currentStep === 0) {
        return "Next";
      }
    }

    return null;
  }

  const disableButton = () => {
    // Remove duplicated results
    const removeDuplicatedResults = results.filter((result, index, self) =>
      index === self.findIndex((r) => r.macAddress === result.macAddress)
    );

    if (adoptionSource === adoptionSourceType.byRow) {
      if (scopeLevel === scopeLevelOption.org) {
        if (hasSshDevices && !sshStatus) {
          return true;
        }

        if (selectedSite?.id === 'none') {
          return true;
        }
      } else {
        if (hasSshDevices && !sshStatus) {
          return true;
        }
      }

      if (currentStep === componentsOption[adoptionSource].steps - 1) {
        if (selectedDevice && removeDuplicatedResults.length !== 1) {
          return true;
        }
      }
    }

    if (adoptionSource === adoptionSourceType.byCheckboxWithIpSettings) {
      // Remove devices that are not selected from removeDeduplicatedResults
      const selectedDevicesMacs = selectedDevices.map(d => d.macAddress);
      const filteredResults = removeDuplicatedResults.filter(result => selectedDevicesMacs.includes(result.macAddress));

      if (currentStep === 0 && !ipSettingsStatus) {
        return true;
      }

      if (currentStep === componentsOption[adoptionSource].steps - 1) {
        if (selectedDevices && filteredResults.length !== selectedDevices.length) {
          return true;
        }
      }
    }

    if (adoptionSource === adoptionSourceType.byCheckboxWithAuthenticate) {
      // Remove devices that are not selected from removeDeduplicatedResults
      const selectedDevicesMacs = selectedDevices.map(d => d.macAddress);
      const filteredResults = removeDuplicatedResults.filter(result => selectedDevicesMacs.includes(result.macAddress));

      if (currentStep === 0 && !authenticateStatus) {
        return true;
      }

      if (currentStep === componentsOption[adoptionSource].steps - 1) {
        if (selectedDevices && filteredResults.length !== selectedDevices.length) {
          return true;
        }
      }
    }

    if (adoptionSource === adoptionSourceType.byCheckboxWithAdoption) {
      // Remove devices that are not selected from removeDeduplicatedResults
      const selectedDevicesMacs = selectedDevices.map(d => d.macAddress);
      const filteredResults = removeDuplicatedResults.filter(result => selectedDevicesMacs.includes(result.macAddress));

      if (currentStep === 0) {
        if (scopeLevel === scopeLevelOption.org) {
          if (hasSshDevices && !sshStatus) {
            return true;
          }

          if (selectedSite?.id === 'none') {
            return true;
          }
        } else {
          if (hasSshDevices && !sshStatus) {
            return true;
          }
        }
      }

      if (currentStep === componentsOption[adoptionSource].steps - 1) {
        if (selectedDevices && filteredResults.length !== selectedDevices.length) {
          return true;
        }
      }
    }

    return false;
  }



  // 真正發送採納請求與進入下一階段
  const proceedAdoption = () => {
    const nextStep = currentStep + 1;

    let selectedSiteId = null;
    if (selectedSite) {
      selectedSiteId = selectedSite.id;
    } else if (sites && sites.length > 0) {
      selectedSiteId = sites[0].id;
    }

    let selectedIpMode = null;
    if (ipModeOptions && ipModeOptions.length > 0) {
      selectedIpMode = ipModeOptions.find(option => option.selected);
    }

    setCurrentStep(nextStep);
    if (nextStep === componentsOption[adoptionSource].steps - 1) {
      sendAdoptionRequest(
        adoptionSource, selectedDevices, selectedDevice,
        selectedSiteId, username, password,
        selectedIpMode, ipAddress, subnetMask, gateway, dns1, dns2,
        sshUsername, sshPassword,
      );
    }
  };

  const callbackFn = async () => {
    let selectedSiteId = null;

    if (selectedSite && selectedSite.id !== 'none') {
      selectedSiteId = selectedSite.id;
    } else if (sites && sites.length > 0) {
      selectedSiteId = sites[0].id;
    }

    const devicesToAdopt = adoptionSource === adoptionSourceType.byRow ? [selectedDevice] : selectedDevices;

    const hasSiteTarget = scopeLevel === scopeLevelOption.site
      ? true
      : Boolean(selectedSiteId && selectedSiteId !== "inventory");
    const isAdoptionFlow =
      adoptionSource === adoptionSourceType.byRow ||
      adoptionSource === adoptionSourceType.byCheckboxWithAdoption;

    if (isAdoptionFlow && licenseManagementEnabled && hasSiteTarget) {
      sendPreConfirmRequest(selectedSiteId, devicesToAdopt);
    } else {
      proceedAdoption();
    }
  };

  useEffect(() => {
    if (preConfirmResult) {
      const data = preConfirmResult.data || [];

      const hasSubscribedService = data.some(item => item.service?.length > 0);

      setActivePreConfirmData(data);
      setPreConfirmResult(null);

      if (hasSubscribedService) {
        setShowConfirmServiceChangeModal(true);
      } else {
        proceedAdoption();
      }
    }
  }, [preConfirmResult]);

  // Side effect
  useEffect(() => {
    if (showModalAdoption) {
      // Set initial states
      const updatedReadyDevices = [];
      setIpSettingsStatus(false);

      if (adoptionSource === adoptionSourceType.byRow) {
        // By row
        updatedReadyDevices.push({
          ...selectedDevice,
        });

        selectedDevice.protocolType === protocolTypeOption.snmp ? setHasSshDevices(true) : setHasSshDevices(false);
      } else {
        // Batch action
        let hasSshDevice = false;
        selectedDevices.forEach((device) => {
          const updatedDevice = {
            ...device,
          };
          updatedReadyDevices.push(updatedDevice);

          if (device.protocolType === protocolTypeOption.snmp) {
            hasSshDevice = true;
          }
        });

        setHasSshDevices(hasSshDevice);

        devices.forEach((device) => {
          // Check if the device is already in the selected devices
          const isDeviceInSelected = some(selectedDevices, { macAddress: device.macAddress });
          if (isDeviceInSelected) {
            return;
          }

          if (device.prevChecked) {
            const updatedDevice = {
              ...device,
            };
            updatedReadyDevices.push(updatedDevice);
          }
        });
      }

      setReadyDevices(updatedReadyDevices);

      // Components declared by adoption source
      const updatedComponents = componentsOption[adoptionSource].components[currentStep];
      setComponents(updatedComponents);
    }
  }, [adoptionSource, currentStep, devices, selectedDevice, selectedDevices, setReadyDevices, showModalAdoption]);

  if (isPreConfirmLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!components || components.length === 0) {
    return null;
  }

  return (
    <>
      {!showConfirmServiceChangeModal && components.map((component, index) => {
        switch (component) {
          case 'AdoptionTitle':
            return (
              <AdoptionTitle
                key={`${index}-${component}`}
                adoptionSource={adoptionSource}
                currentStep={currentStep}
                totalSteps={componentsOption[adoptionSource].steps - 1}
                closeOpenModal={closeOpenModal}
                disabledBtn={disableButton()}
                sites={sites}
              />
            );
          case 'DeviceTable':
            return (
              <DeviceTable
                key={`${index}-${component}`}
                currentStep={currentStep}
                totalSteps={componentsOption[adoptionSource].steps - 1}
                devices={readyDevices}
                results={results}
              />
            );
          case 'SiteSetting':
            return (
              scopeLevel === scopeLevelOption.site
              && !hasSshDevices
            ) ? (
              <div
                key={`${index}-${component}-none`}
                style={{ display: 'none' }}
              />
            ) : (
              <SiteSetting
                key={`${index}-${component}`}
                scopeLevel={scopeLevel}
                adoptionSource={adoptionSource}
                hasSshDevices={hasSshDevices}
                sshAccount={sshUsername}
                setSshAccount={setSshUsername}
                sshPassword={sshPassword}
                setSshPassword={setSshPassword}
                setSshStatus={setSshStatus}
                currentStep={currentStep}
                totalSteps={componentsOption[adoptionSource].steps - 1}
                sites={sites}
                selectedSite={selectedSite}
                setSelectedSite={setSelectedSite}
              />
            );
          case 'IpSettings':
            return (
              <IpSettings
                key={`${index}-${component}`}
                ipModeOptions={ipModeOptions}
                setIpModeOptions={setIpModeOptions}
                ipAddress={ipAddress}
                setIpAddress={setIpAddress}
                subnetMask={subnetMask}
                setSubnetMask={setSubnetMask}
                gateway={gateway}
                setGateway={setGateway}
                dns1={dns1}
                setDns1={setDns1}
                dns2={dns2}
                setDns2={setDns2}
                currentStep={currentStep}
                totalSteps={componentsOption[adoptionSource].steps - 1}
                setIpSettingsStatus={setIpSettingsStatus}
                isAllNotSupportDNSDevice={isAllNotSupportDNSDevice}
              />
            );
          case 'Authentication':
            return (
              <Authentication
                key={`${index}-${component}`}
                username={username}
                setUsername={setUsername}
                password={password}
                setPassword={setPassword}
                setAuthenticateStatus={setAuthenticateStatus}
              />
            );
          case 'Operation':
            return (
              <Operation
                key={`${index}-${component}`}
                closeOpenModal={closeOpenModal}
                adoptionSource={adoptionSource}
                currentStep={currentStep}
                totalSteps={componentsOption[adoptionSource].steps - 1}
                scopeLevel={scopeLevel}
                btnTitle={getButtonTitle()}
                disabledBtn={disableButton()}
                callbackFn={callbackFn}
              />
            );
          default:
            return null;
        }
      })}

      {(() => {
        const serviceNameMapping = {
          healthAI: "Health AI Insight",
          nmsPro: "NMS Pro"
        };

        const sitesToRender = activePreConfirmData ? activePreConfirmData.filter(item => item.service && item.service.length > 0) : [];
        const isInsufficient = sitesToRender.some(item => item.sufficientCredits === false);

        return (
          <Box
            style={{ display: showConfirmServiceChangeModal ? 'block' : 'none' }}
          >
            <Box sx={{ position: "relative" }}>
              <Stack spacing={2.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ color: "#172664", fontSize: "18px", fontWeight: "bold" }}>
                    Confirm Service Change
                  </Typography>
                  <IconButton onClick={() => setShowConfirmServiceChangeModal(false)} sx={{ p: 0.5 }}>
                    <CloseOutlined style={{ fontSize: "18px", color: "rgba(0,0,0,0.45)" }} />
                  </IconButton>
                </Stack>

                <Box sx={{ overflowY: "auto", maxHeight: "75vh" }}>
                  <Stack spacing={2}>
                    {sitesToRender.map((siteItem, idx) => {
                      const serviceText = siteItem.service.map(s => serviceNameMapping[s] || s).join(", ") || "Subscribed Services";
                      const actionText = siteItem.action === "add" ? "Added Device" : siteItem.action === "remove" ? "Remove Device" : siteItem.action;
                      const dailyRateValue = siteItem.dailyRate || 0;
                      const totalCredits = dailyRateValue * (siteItem.remainingDays || 0);

                      return (
                        <Stack key={idx} spacing={2} sx={{ mb: idx < sitesToRender.length - 1 ? 3 : 0 }}>
                          <Stack spacing={1}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <CaretRightFilled style={{ fontSize: "14px", color: "#172664" }} />
                              <Typography sx={{ color: "rgba(0,0,0,0.85)", fontSize: "15px", fontWeight: "bold" }}>
                                {siteItem.siteName || "Site"}
                              </Typography>
                            </Stack>
                            <Typography sx={{ color: "#172664", fontSize: "16px", fontWeight: "bold", mb: 0.5 }}>
                              Service: {serviceText}
                            </Typography>
                            <Divider sx={{ mb: 1.5 }} />

                            <Stack spacing={0.5}>
                              <Stack direction="row" spacing={1}>
                                <Typography variant="body2" sx={{ minWidth: "65px", color: "rgba(0,0,0,0.45)" }}>
                                  Action:
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#172664", fontWeight: "bold" }}>
                                  {actionText}
                                </Typography>
                              </Stack>
                              <Stack direction="row" spacing={1}>
                                <Typography variant="body2" sx={{ minWidth: "65px", color: "rgba(0,0,0,0.45)" }}>
                                  New Devices:
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#008514", fontWeight: "bold" }}>
                                  {siteItem.deviceCount} (Total: {siteItem.deviceTotalCount})
                                </Typography>
                              </Stack>
                              <Stack direction="row" spacing={1}>
                                <Typography variant="body2" sx={{ minWidth: "65px", color: "rgba(0,0,0,0.45)" }}>
                                  Rate:
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#373737", fontWeight: "bold" }}>
                                  {siteItem.rate || ""}
                                </Typography>
                              </Stack>
                            </Stack>
                          </Stack>

                          {/* This Month’s Prorated Credit Deduction */}
                          <Box sx={{ p: 2, border: "1px solid rgba(0,0,0,0.15)", borderRadius: "12px" }}>
                            <Typography sx={{ color: "#172664", fontSize: "14px", fontWeight: "bold", mb: 2 }}>
                              This Month’s Prorated Credit Deduction
                            </Typography>
                            <Stack spacing={1.5}>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" color="textSecondary">
                                  Daily Rate
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: "bold", color: "#373737" }}>
                                  <Box component="span" sx={{ color: "#008514" }}>
                                    {dailyRateValue.toLocaleString()}
                                  </Box> Credits / day
                                </Typography>
                              </Stack>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" color="textSecondary">
                                  Remaining Days
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: "bold", color: "#373737" }}>
                                  <Box component="span" sx={{ color: "#008514" }}>
                                    {siteItem.remainingDays}
                                  </Box> Days
                                </Typography>
                              </Stack>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" color="textSecondary">
                                  Total
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: "bold", color: "#373737" }}>
                                  <Box component="span" sx={{ color: "#008514" }}>
                                    {totalCredits.toLocaleString()}
                                  </Box> Credits
                                </Typography>
                              </Stack>
                            </Stack>
                          </Box>
                        </Stack>
                      );
                    })}

                    {/* Warning Section */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                      <WarningFilled style={{ color: "#D1003B", fontSize: "18px" }} />
                      <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.85)", fontWeight: "bold" }}>
                        {isInsufficient ? "Insufficient credits. Please contact your Organization Super Admin." : "Changes take effect immediately upon confirmation."}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>

                {/* Actions 按鈕 */}
                <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 1 }}>
                  <OutlineButton
                    onClick={() => setShowConfirmServiceChangeModal(false)}
                    width="120px"
                    height="36px"
                    sx={{ borderRadius: "20px" }}
                  >
                    Cancel
                  </OutlineButton>
                  <SolidButton
                    onClick={() => {
                      setShowConfirmServiceChangeModal(false);
                      proceedAdoption();
                    }}
                    disabled={isInsufficient}
                    width="120px"
                    height="36px"
                    sx={{ borderRadius: "20px", bgcolor: "#172664" }}
                  >
                    Confirm
                  </SolidButton>
                </Stack>
              </Stack>
            </Box >
          </Box >
        );
      })()}
    </>
  );
}

export default ModalAdoption;
